import React from 'react';
import { Archive, FolderInput, Undo2 } from 'lucide-react';

/**
 * Moving the open message: Archive, Move to…, and Undo after a move.
 *
 * Moves go by folder id only (the service never takes a folder name, which
 * Graph would create on a miss). Deleted Items is not offered: deleting waits
 * on who at the client may delete (2026-09-29), and the service refuses it too.
 */

// Folders a message is not moved into from here.
const NOT_TARGETS = new Set(['deleted items', 'drafts', 'outbox']);

/** Whether a message may be moved into this folder: by Move to… or by a drop. */
export const canMoveInto = (folder) => !NOT_TARGETS.has(folder.name.toLowerCase());

/** Every folder the tree has loaded, subfolders labelled with their parents. */
export const folderChoices = (roots, children) => {
  const out = [];
  const walk = (list, prefix) => {
    list.forEach((f) => {
      const label = prefix ? `${prefix} / ${f.name}` : f.name;
      if (canMoveInto(f)) out.push({ id: f.id, label });
      if (children[f.id]) walk(children[f.id], label);
    });
  };
  walk(roots, '');
  return out;
};

/** The message a failed move leaves: why, for a person. */
export const moveError = (err, fallback) => {
  const status = err?.response?.status;
  if (status === 404) return 'This message has moved since. Refresh to see where it is.';
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const btn =
  'inline-flex shrink-0 items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50';

// `compact`: icons only, the words kept for screen readers and as a tooltip.
const iconBtn =
  'inline-flex shrink-0 items-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50';

export const MoveControls = ({
  message,
  choices,
  archiveId,
  busy,
  onMove,
  onOpenPicker,
  compact = false,
}) => {
  const inArchive = archiveId && message.folder_id === archiveId;
  const cls = compact ? iconBtn : btn;
  const icon = compact ? 'h-4 w-4' : 'h-3.5 w-3.5';
  const word = (text) => (compact ? <span className="sr-only">{text}</span> : text);
  return (
    <>
      {!inArchive && (
        <button
          type="button"
          className={cls}
          disabled={busy}
          onClick={() => onMove('archive')}
          title={compact ? 'Archive' : undefined}
        >
          <Archive className={icon} />
          {word('Archive')}
        </button>
      )}
      <label className={`${cls} relative cursor-pointer`} title="Move to another folder">
        <FolderInput className={icon} />
        {word('Move to…')}
        <select
          aria-label="Move to folder"
          value=""
          disabled={busy}
          onFocus={onOpenPicker}
          onMouseDown={onOpenPicker}
          onChange={(e) => e.target.value && onMove(e.target.value)}
          className="absolute inset-0 cursor-pointer opacity-0"
        >
          <option value="">Move to…</option>
          {choices
            .filter((f) => f.id !== message.folder_id)
            .map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
        </select>
      </label>
    </>
  );
};

export const UndoBar = ({ move, busy, onUndo, onDismiss }) => (
  <div className="mb-3 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm text-blue-900">
    <span className="min-w-0 flex-1 truncate">Moved to {move.toName}.</span>
    <button
      type="button"
      onClick={onUndo}
      disabled={busy}
      className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
    >
      <Undo2 className="h-3.5 w-3.5" />
      Undo
    </button>
    <button
      type="button"
      onClick={onDismiss}
      className="shrink-0 rounded px-1 text-blue-700 hover:bg-blue-100"
      aria-label="Dismiss"
    >
      ×
    </button>
  </div>
);
